// framework/client/components/RoleSwitcher.js
import { ComponentBase } from "../core/ComponentBase.js";

export class RoleSwitcher extends ComponentBase {
    static styles = `
        .role-switcher-container .dropdown-toggle {
            font-size: var(--ab-text-base);
            font-weight: 500;
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }
        .role-switcher-container .dropdown-toggle i {
            font-size: var(--ab-text-lg);
        }
        .role-switcher-container .dropdown-menu {
            min-width: 12rem;
        }
        .role-switcher-container .dropdown-item {
            display: flex;
            align-items: center;
            gap: 0.75rem;
        }
        .role-switcher-container .dropdown-item i {
            font-size: var(--ab-text-lg);
            width: 1.2rem;
            text-align: center;
            color: var(--bs-secondary-color);
        }
        .dropdown-item.active i, .dropdown-item:active i {
            color: inherit;
        }

        .role-switcher-btn {
             height: 30px !important; /* Standard Bootstrap button height */
            min-width: 30px !important; /* Ensure icon-only buttons are square */
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            padding: 0.375rem 0.75rem !important; /* Standard Bootstrap padding */
            line-height: 1.5 !important;
        }
        /* --- NEW: Responsive Styles for Mobile View --- */
        @media (max-width: 767.98px) {
            .role-switcher-container .dropdown-toggle .role-switcher-label {
                display: none; /* Hide the text label on smaller screens */
            }
            .role-switcher-container .dropdown-toggle {
                padding-left: 0.5rem;  /* Adjust padding for a more compact icon-only look */
                padding-right: 0.5rem;
            }
        }
    `;


    constructor(config) {
        super(config);
        this.availableRoles = [];
        this.activeRoleName = null;
        this._boundUpdateStateAndRender = this._updateStateAndRender.bind(this);
        this.applyStyles();
    }

    static getDefaultConfig() {
        return {
            rolesConfig: null,
            hideFor: [],
            roleIcons: {},
            // NEW: Explicitly define which roles are switchable.
            // If null, it will fall back to iterating all defaultRoutes.
            switchableRoles: null,
        };
    }

    async initialize() {
        this.appClient.events.local.on("USER_LOGGED_IN", this._boundUpdateStateAndRender);
        this.appClient.events.local.on("USER_LOGGED_OUT", this._boundUpdateStateAndRender);
        this.appClient.events.local.on("route-changed", this._boundUpdateStateAndRender);
        await super.initialize();
    }

    async initAfterLogin() {
        await this._updateStateAndRender();
    }

    async _updateStateAndRender() {
        const user = this.appClient.getSharedData("user");
        if (!user) {
            this.availableRoles = [];
            this.render();
            return;
        }
        const userRoles = Array.isArray(user.role) ? user.role : [user.role];
        const primaryRole = userRoles[0];

        if (this.config.hideFor.includes(primaryRole)) {
            this.availableRoles = [];
            this.render();
            return;
        }

        // --- THE CORE FIX ---
        // Determine the list of roles to consider for the switcher.
        // 1. If `switchableRoles` prop is provided, use it as the definitive list.
        // 2. Otherwise, fall back to all keys from the merged `defaultRoutes`.
        const defaultRoutes = this.appClient.layoutEngine.defaultRoutes;
        const roleNamesToConsider = this.config.switchableRoles || Object.keys(defaultRoutes);

        let sourceRoles = [];
        for (const roleName of roleNamesToConsider) {
            // Always filter out non-navigable utility routes.
            if (roleName === 'public' || roleName === 'login' || roleName === 'entry') continue;

            const routePath = defaultRoutes[roleName];
            if (routePath) { // Ensure the role has a corresponding route
                sourceRoles.push({
                    name: roleName,
                    label: this.t(roleName.charAt(0).toUpperCase() + roleName.slice(1)),
                    route: routePath,
                    icon: this.config.roleIcons[roleName] || 'bi-person-workspace'
                });
            }
        }
        // --- END OF FIX ---

        const permittedRoles = [];
        for (const role of sourceRoles) {
            const hasPerm = await this.appClient.sessionTokenHandler.hasPermission('routes', role.route);
            if (hasPerm) {
                permittedRoles.push(role);
            }
        }
        this.availableRoles = permittedRoles;

        const currentRoute = this.appClient.layoutEngine.currentRoute || '';
        let activeRole = null;
        let bestMatchLength = 0;

        for (const role of this.availableRoles) {
            const baseSegment = '/' + (role.route.split('/')[1] || '');
            if (baseSegment !== '/' && currentRoute.startsWith(baseSegment)) {
                if (baseSegment.length > bestMatchLength) {
                    activeRole = role;
                    bestMatchLength = baseSegment.length;
                }
            }
        }
        this.activeRoleName = activeRole ? activeRole.name : null;

        this.render();
    }

    render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'role-switcher-container dropdown';
        }
        this.container.innerHTML = '';

        if (this.availableRoles.length <= 1) {
            return this.container;
        }

        const activeRole = this.availableRoles.find(r => r.name === this.activeRoleName) || this.availableRoles[0];

        const button = document.createElement('button');
        button.className = 'btn btn-outline-secondary role-switcher-btn header-btn dropdown-toggle';
        button.type = 'button';
        button.setAttribute('data-bs-toggle', 'dropdown');
        button.setAttribute('aria-expanded', 'false');
        button.innerHTML = `<i class="${activeRole.icon}"></i> <span class="role-switcher-label">${this.t(activeRole.label)}</span>`;

        const menu = document.createElement('ul');
        menu.className = 'dropdown-menu dropdown-menu-end';

        this.availableRoles.forEach(role => {
            const li = document.createElement('li');
            const a = document.createElement('a');
            a.className = `dropdown-item ${role.name === this.activeRoleName ? 'active' : ''}`;
            a.href = '#';
            a.innerHTML = `<i class="${role.icon}"></i> ${this.t(role.label)}`;
            a.addEventListener('click', (e) => {
                e.preventDefault();
                this.appClient.layoutEngine.navigate(role.route);
            });
            li.appendChild(a);
            menu.appendChild(li);
        });

        this.container.appendChild(button);
        this.container.appendChild(menu);

        this.appClient.tx.applyTranslations(this.container);
        return this.container;
    }

    destroy() {
        this.appClient.events.local.off("USER_LOGGED_IN", this._boundUpdateStateAndRender);
        this.appClient.events.local.off("USER_LOGGED_OUT", this._boundUpdateStateAndRender);
        this.appClient.events.local.off("route-changed", this._boundUpdateStateAndRender);
        super.destroy();
    }
}